import {
    type AiModelChoice,
    type AiProviderId,
    DEEPSEEK_MODELS,
    GEMINI_MODELS,
    GEMINI_SEARCH_MODELS,
    OPENAI_MODELS,
    isModelUnavailableError,
    isSearchCapableAiChoice,
    listAiModelChoices,
    modelsWithPreferred,
    withModelFallback,
} from "@/lib/ai/modelCatalog";

const DEEPSEEK_API_KEY = (import.meta.env.DEEPSEEK_API_KEY as string | undefined) ?? "";
const OPENAI_API_KEY = (import.meta.env.OPENAI_API_KEY as string | undefined) ?? "";
const GEMINI_API_KEY = (import.meta.env.GEMINI_API_KEY as string | undefined) ?? "";

export interface ChatMessage {
    role: "system" | "user" | "assistant";
    content: string;
}

export function isDeepSeekConfigured(): boolean {
    return Boolean(DEEPSEEK_API_KEY);
}

export function isOpenAiConfigured(): boolean {
    return Boolean(OPENAI_API_KEY);
}

export function isGeminiConfigured(): boolean {
    return Boolean(GEMINI_API_KEY);
}

export function isProviderConfigured(provider: AiProviderId): boolean {
    if (provider === "deepseek") return isDeepSeekConfigured();
    if (provider === "openai") return isOpenAiConfigured();
    return isGeminiConfigured();
}

export function isAnyAiConfigured(): boolean {
    return isDeepSeekConfigured() || isOpenAiConfigured() || isGeminiConfigured();
}

/** Models from the shared catalog whose API keys are present in this environment. */
export function listConfiguredAiModelChoices(): AiModelChoice[] {
    return listAiModelChoices().filter((choice) => isProviderConfigured(choice.provider));
}

export function listConfiguredSearchAiModelChoices(): AiModelChoice[] {
    return listConfiguredAiModelChoices().filter((choice) => isSearchCapableAiChoice(choice));
}

export function isWebSearchConfigured(): boolean {
    return isOpenAiConfigured() || isGeminiConfigured();
}

async function chatCompletions(params: {
    url: string;
    apiKey: string;
    model: string;
    messages: ChatMessage[];
    jsonMode?: boolean;
    thinking?: { type: "enabled" | "disabled" };
    signal?: AbortSignal;
}): Promise<string> {
    const body: Record<string, unknown> = {
        model: params.model,
        messages: params.messages,
    };

    if (params.jsonMode) {
        body.response_format = { type: "json_object" };
    }

    if (params.thinking) {
        body.thinking = params.thinking;
    }

    const response = await fetch(params.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${params.apiKey}`,
        },
        body: JSON.stringify(body),
        signal: params.signal,
    });

    if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(`AI request failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`);
    }

    const data = (await response.json()) as { choices?: Array<{ message?: { content?: string } }> };
    const content = data.choices?.[0]?.message?.content;
    if (!content) {
        throw new Error("AI returned an empty response.");
    }

    return content;
}

export interface SearchGrounding {
    queries: string[];
    sources: Array<{ title: string; uri: string }>;
    searchCalls: number;
}

export type GeminiGrounding = SearchGrounding;

export interface SearchChatResult {
    content: string;
    grounding: SearchGrounding;
}

export type GeminiGenerateResult = SearchChatResult;

type GeminiGeneratePayload = {
    usageMetadata?: {
        billedToolCalls?: unknown;
    };
    candidates?: Array<{
        content?: { parts?: Array<{ text?: string; thought?: boolean }> };
        groundingMetadata?: {
            webSearchQueries?: string[];
            groundingChunks?: Array<{
                web?: { title?: string; uri?: string };
                retrievedContext?: { title?: string; uri?: string };
            }>;
            groundingSupports?: unknown[];
            searchEntryPoint?: { renderedContent?: string };
        };
    }>;
};

function hasBilledToolCalls(value: unknown): boolean {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
}

export function parseGeminiGenerateContent(data: GeminiGeneratePayload): GeminiGenerateResult {
    const candidate = data.candidates?.[0];
    const content = candidate?.content?.parts
        ?.filter((part) => !part.thought)
        .map((part) => part.text?.trim() ?? "")
        .filter(Boolean)
        .join("\n");
    if (!content) {
        throw new Error("Gemini returned an empty response.");
    }

    const grounding = candidate?.groundingMetadata;
    const queries = (grounding?.webSearchQueries ?? []).map((query) => query.trim()).filter(Boolean);
    const sources = (grounding?.groundingChunks ?? [])
        .map((chunk) => ({
            title: chunk.web?.title?.trim() || chunk.retrievedContext?.title?.trim() || "",
            uri: chunk.web?.uri?.trim() || chunk.retrievedContext?.uri?.trim() || "",
        }))
        .filter((source) => source.uri);
    const groundedInText = /vertexaisearch\.cloud\.google\.com|grounding-api-redirect/i.test(content);
    const searchCalls =
        queries.length ||
        (sources.length ? 1 : 0) ||
        (grounding?.searchEntryPoint?.renderedContent ||
        (grounding?.groundingSupports?.length ?? 0) > 0 ||
        hasBilledToolCalls(data.usageMetadata?.billedToolCalls) ||
        groundedInText
            ? 1
            : 0);

    return {
        content,
        grounding: { queries, sources, searchCalls },
    };
}

async function geminiGenerateContent(
    model: string,
    prompt: string,
    jsonMode: boolean,
    googleSearch = false
): Promise<GeminiGenerateResult> {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(GEMINI_API_KEY)}`;

    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            ...(jsonMode && !googleSearch ? { generationConfig: { responseMimeType: "application/json" } } : {}),
            ...(googleSearch ? { tools: [{ google_search: {} }] } : {}),
        }),
    });

    if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(`Gemini request failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`);
    }

    return parseGeminiGenerateContent((await response.json()) as GeminiGeneratePayload);
}

function buildGeminiPrompt(messages: ChatMessage[]): string {
    const systemParts = messages.filter((m) => m.role === "system").map((m) => m.content);
    const nonSystem = messages.filter((m) => m.role !== "system");
    return [
        systemParts.length ? systemParts.join("\n\n") : "",
        ...nonSystem.map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`),
    ]
        .filter(Boolean)
        .join("\n\n");
}

export async function deepseekChat(
    messages: ChatMessage[],
    jsonMode = true,
    signal?: AbortSignal,
    preferredModel?: string
): Promise<string> {
    if (!DEEPSEEK_API_KEY) {
        throw new Error("DEEPSEEK_API_KEY is not configured.");
    }

    return withModelFallback(modelsWithPreferred(preferredModel, DEEPSEEK_MODELS), "DeepSeek", (model) =>
        chatCompletions({
            url: "https://api.deepseek.com/chat/completions",
            apiKey: DEEPSEEK_API_KEY,
            model,
            messages,
            jsonMode,
            thinking: { type: "disabled" },
            signal,
        })
    );
}

export async function openAiChat(messages: ChatMessage[], jsonMode = true, preferredModel?: string): Promise<string> {
    if (!OPENAI_API_KEY) {
        throw new Error("OPENAI_API_KEY is not configured.");
    }

    return withModelFallback(modelsWithPreferred(preferredModel, OPENAI_MODELS), "OpenAI", (model) =>
        chatCompletions({
            url: "https://api.openai.com/v1/chat/completions",
            apiKey: OPENAI_API_KEY,
            model,
            messages,
            jsonMode,
        })
    );
}

export async function geminiChat(
    messages: ChatMessage[],
    jsonMode = true,
    preferredModel?: string,
    googleSearch = false
): Promise<string> {
    if (!GEMINI_API_KEY) {
        throw new Error("GEMINI_API_KEY is not configured.");
    }

    const prompt = buildGeminiPrompt(messages);

    const result = await withModelFallback(modelsWithPreferred(preferredModel, GEMINI_MODELS), "Gemini", (model) =>
        geminiGenerateContent(model, prompt, jsonMode, googleSearch)
    );
    return result.content;
}

/** Gemini with Google Search, skipping flash-lite. Returns grounding so callers can see if it actually searched. */
export async function geminiSearchChat(messages: ChatMessage[], preferredModel?: string): Promise<SearchChatResult> {
    if (!GEMINI_API_KEY) {
        throw new Error("GEMINI_API_KEY is not configured.");
    }

    const preferred = preferredModel && !preferredModel.includes("lite") ? preferredModel : undefined;
    const prompt = buildGeminiPrompt(messages);

    return withModelFallback(modelsWithPreferred(preferred, GEMINI_SEARCH_MODELS), "Gemini", (model) =>
        geminiGenerateContent(model, prompt, false, true)
    );
}

type OpenAiResponsesPayload = {
    output_text?: string;
    output?: Array<{
        type?: string;
        action?: {
            type?: string;
            query?: string;
            queries?: string[];
            sources?: Array<{ url?: string; title?: string }>;
        };
        content?: Array<{
            type?: string;
            text?: string;
            annotations?: Array<{ type?: string; url?: string; title?: string }>;
        }>;
    }>;
};

export function parseOpenAiResponsesSearch(data: OpenAiResponsesPayload): SearchChatResult {
    const items = data.output ?? [];
    const searchItems = items.filter((item) => item.type === "web_search_call");
    const queries = searchItems.flatMap((item) => {
        const action = item.action;
        if (!action) return [];
        if (action.query?.trim()) return [action.query.trim()];
        return (action.queries ?? []).map((query) => query.trim()).filter(Boolean);
    });
    const sources = [
        ...searchItems.flatMap((item) =>
            (item.action?.sources ?? []).map((source) => ({
                title: source.title?.trim() ?? "",
                uri: source.url?.trim() ?? "",
            }))
        ),
        ...items.flatMap((item) =>
            (item.content ?? []).flatMap((part) =>
                (part.annotations ?? [])
                    .filter(
                        (annotation) =>
                            Boolean(annotation.url) && (annotation.type === "url_citation" || !annotation.type)
                    )
                    .map((annotation) => ({
                        title: annotation.title?.trim() ?? "",
                        uri: annotation.url?.trim() ?? "",
                    }))
            )
        ),
    ].filter((source) => source.uri);

    const uniqueSources = [...new Map(sources.map((source) => [source.uri, source])).values()];

    const content =
        data.output_text?.trim() ||
        items
            .flatMap((item) => (item.content ?? []).map((part) => part.text?.trim() ?? ""))
            .filter(Boolean)
            .join("\n");

    if (!content) {
        throw new Error("OpenAI returned an empty response.");
    }

    return {
        content,
        grounding: {
            queries,
            sources: uniqueSources,
            searchCalls: searchItems.length || (uniqueSources.length ? 1 : 0),
        },
    };
}

function isOpenAiSearchFallbackError(error: unknown): boolean {
    if (isModelUnavailableError(error)) return true;
    const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
    return (
        (message.includes("web_search") || message.includes("tool_choice") || message.includes("include")) &&
        (message.includes("unsupported") ||
            message.includes("not supported") ||
            message.includes("invalid") ||
            message.includes("unknown"))
    );
}

async function openAiResponsesSearch(model: string, messages: ChatMessage[]): Promise<SearchChatResult> {
    const instructions = messages
        .filter((message) => message.role === "system")
        .map((message) => message.content)
        .join("\n\n");
    const input = messages
        .filter((message) => message.role !== "system")
        .map((message) => message.content)
        .join("\n\n");

    const response = await fetch("https://api.openai.com/v1/responses", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
            model,
            instructions,
            input,
            tools: [{ type: "web_search", search_context_size: "high" }],
            tool_choice: "required",
            include: ["web_search_call.action.sources"],
        }),
    });

    if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(`OpenAI request failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`);
    }

    return parseOpenAiResponsesSearch((await response.json()) as OpenAiResponsesPayload);
}

/** OpenAI Responses API with web_search. */
export async function openAiSearchChat(messages: ChatMessage[], preferredModel?: string): Promise<SearchChatResult> {
    if (!OPENAI_API_KEY) {
        throw new Error("OPENAI_API_KEY is not configured.");
    }

    const models = modelsWithPreferred(preferredModel, OPENAI_MODELS);
    let lastError: Error | undefined;

    for (const model of models) {
        try {
            return await openAiResponsesSearch(model, messages);
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            if (!isOpenAiSearchFallbackError(lastError)) {
                throw lastError;
            }
        }
    }

    throw lastError ?? new Error("OpenAI: no models configured.");
}

export async function searchChat(
    messages: ChatMessage[],
    options: { provider: "openai" | "gemini"; model?: string }
): Promise<SearchChatResult> {
    if (options.provider === "openai") {
        return openAiSearchChat(messages, options.model);
    }
    return geminiSearchChat(messages, options.model);
}

/** Call one catalog provider, trying the selected model first then that provider's fallbacks. */
export async function chatWithModel(
    messages: ChatMessage[],
    options: {
        provider: AiProviderId;
        model?: string;
        jsonMode?: boolean;
        signal?: AbortSignal;
        /** Gemini only: ground the answer with Google Search. */
        googleSearch?: boolean;
    }
): Promise<string> {
    const jsonMode = options.jsonMode ?? true;

    if (options.provider === "deepseek") {
        return deepseekChat(messages, jsonMode, options.signal, options.model);
    }
    if (options.provider === "openai") {
        return openAiChat(messages, jsonMode, options.model);
    }
    return geminiChat(messages, jsonMode, options.model, options.googleSearch);
}
